import { Box, LoadingOverlay } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import SetNewPasswordForm, { SetNewPasswordFormData } from 'components/forms/set-new-password/SetNewPasswordForm';
import panic from 'errors/panic';
import SimpleLayout from 'layouts/simple/SimpleLayout';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { EXPIRED_RESET_LINK_PAGE_PATH, LOGIN_PAGE_PATH } from 'routes/paths';
import { ERROR_NOTIFICATION_COLOR, PASSWORD_REUSE_ERROR_CODE } from 'utils/constants';

/**
 * The content of the page where the user sets a new password.
 */
function SetNewPasswordPageContent({ email, token }: { email: string; token: string }) {
  const navigate = useNavigate();
  const { getAction } = useApi();

  const { data, loading, error } = useImmediateAction(() => {
    const checkResetPasswordTokenAction = getAction('AuthCheckResetPasswordToken');
    return checkResetPasswordTokenAction({ payload: { email: email!, token: token! } });
  });

  if (!loading && (!data || error)) {
    return <Navigate to={EXPIRED_RESET_LINK_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function handleSubmit({ password, passwordAgain }: SetNewPasswordFormData) {
    const setNewPasswordAction = getAction('AuthResetPassword');

    return setNewPasswordAction({ payload: { email, token, password, passwordConfirmation: passwordAgain } })
      .then(() => navigate(`${LOGIN_PAGE_PATH.original}?resetPassword=success`))
      .catch((error) => {
        if (error?.response?.data?.error?.code === PASSWORD_REUSE_ERROR_CODE) {
          notifications.show({
            title: 'Chyba!',
            message: 'Nové heslo sa musí líšiť od aktuálneho hesla.',
            color: ERROR_NOTIFICATION_COLOR,
          });
          return;
        }

        panic(error);
      });
  }

  return (
    <SimpleLayout>
      <Box pos="relative">
        <LoadingOverlay visible={loading} />
        <SetNewPasswordForm onSubmit={handleSubmit} />
      </Box>
    </SimpleLayout>
  );
}

/**
 * The page where the user sets a new password.
 *
 */
export default function SetNewPasswordPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  if (!token || !email) {
    return <Navigate to={EXPIRED_RESET_LINK_PAGE_PATH.original} />;
  }

  return <SetNewPasswordPageContent email={email} token={token} />;
}
